import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Glyphicon, Modal, ProgressBar } from 'react-bootstrap';
import { dialogHeaderFooterBgColor } from '../../helpers/colors';
import Button from './Button';

// Editing canvas is square so that AvatarEditor "covers" the crop circle at
// scale 1 for any image aspect ratio (see getInitialSize in react-avatar-editor).
const FRAME = 260;
const BORDER = 28;
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const FALLBACK_COLOR = '#D9EDF7';
// Recompress the cropped image to JPEG only when it is larger than this; below
// it the crop is already small enough to keep as-is.
const RECOMPRESS_THRESHOLD_BYTES = 300 * 1024;
const MIN_JPEG_QUALITY = 0.5;
// Cap the stored image's pixel dimensions. The avatar is shown at <=100px, so
// this is plenty for retina while keeping the file small and quick to load
// (getImage() otherwise returns the crop at the source photo's full resolution).
const OUTPUT_MAX_DIM = 400;

// Loaded lazily on the client only; AvatarEditor touches the DOM and must not be
// imported during server-side rendering.
let AvatarEditor;

// Fallback for browsers without HTMLCanvasElement.toBlob.
//
const dataURLToBlob = (dataUrl) => {
    const [header, body] = dataUrl.split(',');
    const mime = (header.match(/:(.*?);/) || [])[1] || 'image/jpeg';
    const binary = atob(body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }

    return new Blob([bytes], { type: mime });
};

class ProfilePictureEditor extends PureComponent {
    constructor(props) {
        super(props);
        this.onScaleChange = this.onScaleChange.bind(this);
        this.onPositionChange = this.onPositionChange.bind(this);
        this.onSave = this.onSave.bind(this);
        this.setEditorRef = this.setEditorRef.bind(this);
        this.state = {
            ready: !!AvatarEditor,
            // Local "working" flag covering the export/recompress step before the
            // parent's upload (and its `uploading` prop) takes over.
            saving: false,
            scale: props.initialScale || MIN_SCALE,
            position: props.initialPosition || { x: 0.5, y: 0.5 },
        };
    }

    async componentDidMount() {
        if (!AvatarEditor) {
            const mod = await import('react-avatar-editor');
            AvatarEditor = mod.default || mod;
            this.setState({ ready: true });
        }
    }

    componentDidUpdate(prevProps) {
        // A fresh image (initial pick or "Replace photo") resets the crop to defaults;
        // re-opening the same image for adjustment restores the stored crop.
        const imageChanged = prevProps.imageFile !== this.props.imageFile ||
            prevProps.imageUrl !== this.props.imageUrl;
        if (imageChanged) {
            this.setState({
                scale: this.props.initialScale || MIN_SCALE,
                position: this.props.initialPosition || { x: 0.5, y: 0.5 },
            });
        }
    }

    onScaleChange(evt) {
        this.setState({ scale: parseFloat(evt.target.value) });
    }

    onPositionChange(position) {
        this.setState({ position });
    }

    setEditorRef(ref) {
        this.editor = ref;
    }

    fail(error) {
        if (error) {
            console.error(new Error().stack, error);
        }

        this.setState({ saving: false });
        if (this.props.onError) {
            this.props.onError('Could not process the image. Please use "Replace photo" and pick the image again.');
        }
    }

    scaleToMax(srcCanvas, maxDim) {
        const longest = Math.max(srcCanvas.width, srcCanvas.height);
        if (!longest || longest <= maxDim) {
            return srcCanvas;
        }

        const ratio = maxDim / longest;
        const out = document.createElement('canvas');
        out.width = Math.round(srcCanvas.width * ratio);
        out.height = Math.round(srcCanvas.height * ratio);
        out.getContext('2d').drawImage(srcCanvas, 0, 0, out.width, out.height);
        return out;
    }

    toBlobAtQuality(canvas, quality) {
        return new Promise((resolve, reject) => {
            try {
                if (canvas.toBlob) {
                    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
                } else {
                    resolve(dataURLToBlob(canvas.toDataURL('image/jpeg', quality)));
                }
            } catch (error) {
                reject(error);
            }
        });
    }

    async onSave() {
        if (!this.editor) {
            return;
        }

        // Show working feedback immediately; the full-res export + recompress can
        // take a moment before the upload (and the `uploading` prop) begins.
        this.setState({ saving: true });

        // Export only the cropped result (a square covering the crop circle); the
        // original image is never uploaded or stored.
        let canvas;
        try {
            // getImage() returns the crop at the source photo's native resolution;
            // downscale it to a sane avatar size so the stored file stays small.
            canvas = this.scaleToMax(this.editor.getImage(), OUTPUT_MAX_DIM);
        } catch (error) {
            this.fail(error);
            return;
        }

        try {
            // Encode to JPEG, then keep recompressing at lower quality while the
            // result is still larger than the threshold (small crops are left
            // untouched).
            let quality = 0.70;
            let blob = await this.toBlobAtQuality(canvas, quality);
            while (blob && blob.size > RECOMPRESS_THRESHOLD_BYTES && quality > MIN_JPEG_QUALITY) {
                quality = Math.max(MIN_JPEG_QUALITY, quality - 0.1);
                const next = await this.toBlobAtQuality(canvas, quality);
                if (!next) {
                    break;
                }

                blob = next;
            }

            if (!blob) {
                this.fail();
                return;
            }

            // Hand off to the parent, which drives `uploading` from here. Clear
            // the local flag so that if the upload fails (or never starts) the
            // dialog isn't left stuck in a busy state.
            this.props.onSave({ blob });
            this.setState({ saving: false });
        } catch (error) {
            // An existing remote image can't be re-exported from a canvas
            // (cross-origin taint). Keep it as-is and close quietly instead of
            // erroring; new/replaced local images export fine.
            if (this.props.imageUrl && !this.props.imageFile) {
                this.setState({ saving: false });
                this.props.onCancel();
                return;
            }

            this.fail(error);
        }
    }

    render() {
        const { open, imageFile, imageUrl, hasExisting, uploading, progress } = this.props;
        const image = imageFile || imageUrl;
        const busy = !!uploading || this.state.saving;
        const hasPercent = !!uploading && progress > 0 && progress < 100;

        return (
            <Modal show={open} onHide={uploading ? undefined : this.props.onCancel} backdrop="static">
                <Modal.Header style={{ backgroundColor: dialogHeaderFooterBgColor }} closeButton={!busy}>
                    <Modal.Title>Profile picture</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ textAlign: 'center' }}>
                        {this.state.ready && image ? (
                            <AvatarEditor
                                ref={this.setEditorRef}
                                image={image}
                                width={FRAME}
                                height={FRAME}
                                border={BORDER}
                                borderRadius={FRAME / 2}
                                color={[0, 0, 0, 0.55]}
                                backgroundColor={FALLBACK_COLOR}
                                scale={this.state.scale}
                                position={this.state.position}
                                onPositionChange={this.onPositionChange}
                                disableBoundaryChecks={false}
                            />
                        ) : (
                            <div style={{ height: FRAME + BORDER * 2, lineHeight: `${FRAME + BORDER * 2}px`, color: '#888' }}>
                                {this.state.ready ? 'No photo selected — click “Choose photo”.' : 'Loading editor…'}
                            </div>
                        )}
                        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Glyphicon glyph="zoom-out" style={{ marginRight: 8, color: '#888' }} />
                            <input
                                type="range"
                                min={MIN_SCALE}
                                max={MAX_SCALE}
                                step={0.01}
                                value={this.state.scale}
                                onChange={this.onScaleChange}
                                disabled={busy}
                                style={{ width: 220 }}
                            />
                            <Glyphicon glyph="zoom-in" style={{ marginLeft: 8, color: '#888' }} />
                        </div>
                        <p style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                            Drag to reposition, slide to zoom. The circle is what shows on the profile.
                        </p>
                        {busy && (
                            <div style={{ marginTop: 12 }}>
                                <ProgressBar
                                    active
                                    striped
                                    now={hasPercent ? progress : 100}
                                    label={hasPercent ? `Uploading… ${progress}%` : (uploading ? 'Uploading…' : 'Processing…')}
                                />
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: dialogHeaderFooterBgColor }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                            {hasExisting && (
                                <Button bsStyle="danger" icon="trash" label="Delete"
                                        disabled={busy} onClick={this.props.onDelete} />
                            )}
                        </div>
                        <div>
                            <Button icon="picture" label={image ? 'Replace photo' : 'Choose photo'}
                                    disabled={busy} onClick={this.props.onReplace} />
                            {' '}
                            <Button label="Cancel" disabled={busy} onClick={this.props.onCancel} />
                            {' '}
                            <Button bsStyle="primary" icon="ok"
                                    label={busy ? 'Saving…' : 'Save'}
                                    disabled={busy || !this.state.ready || !image}
                                    onClick={this.onSave} />
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

ProfilePictureEditor.propTypes = {
    open: PropTypes.bool,
    imageFile: PropTypes.object,
    imageUrl: PropTypes.string,
    initialScale: PropTypes.number,
    initialPosition: PropTypes.object,
    hasExisting: PropTypes.bool,
    uploading: PropTypes.bool,
    progress: PropTypes.number,
    onSave: PropTypes.func.isRequired,
    onReplace: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onError: PropTypes.func,
};

export default ProfilePictureEditor;
